import { ChevronDown, ChevronLeft, ExternalLink } from "lucide-react";
import { NavLink } from "react-router-dom";

import { useAuth } from "@/shared/contexts/AuthProvider";
import { cn } from "@/shared/utils/helpers";

import logoCircle from "@/assets/logo-circle.png";

import { AccountMenu } from "./AccountMenu";
import { NAV_SECTIONS } from "./nav-config";

/** Banff Airporter brand mark (circular logo). */
function BrandMark() {
  return (
    <img
      src={logoCircle}
      width={38}
      height={38}
      alt="Banff Airporter"
      className="portal-brand__logo"
    />
  );
}

/** Two-letter initials from a display name (e.g. "Lamar Falconer" -> "LF"). */
function initialsFrom(name: string | null | undefined): string {
  if (!name) return "BA";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "BA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export interface SidebarProps {
  /** Desktop icon-rail mode. */
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Off-canvas drawer state (mobile). */
  mobileOpen: boolean;
  /** Called when a nav link is followed on mobile (to close the drawer). */
  onNavigate: () => void;
}

export function Sidebar({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onNavigate,
}: SidebarProps) {
  const { displayName, can } = useAuth();

  // Hide any leaf gated by a permission the user doesn't hold, then drop a
  // section left with no visible items. Placeholder (disabled) items have no
  // requiredPermission, so they're unaffected.
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => !item.requiredPermission || can(item.requiredPermission),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <aside
      className={cn("portal-sidebar", mobileOpen && "is-mobile-open")}
      data-portal-chrome="sidebar"
    >
      <div className="portal-brand">
        <span className="portal-brand__mark">
          <BrandMark />
        </span>
        <span className="portal-brand__name">
          <b>Banff Airporter</b>
          <span>Employee Portal</span>
        </span>
      </div>

      <nav className="portal-nav" aria-label="Primary">
        {visibleSections.map((section, sectionIndex) => (
          <div key={section.label ?? `section-${sectionIndex}`}>
            {section.label ? (
              <div className="portal-nav__label">{section.label}</div>
            ) : null}
            {section.items.map((item) => {
              const Icon = item.icon;
              if (item.disabled) {
                return (
                  <button
                    key={item.label}
                    type="button"
                    className="portal-nav-item is-disabled"
                    aria-disabled
                    title={`${item.label} — coming soon`}
                  >
                    <Icon aria-hidden />
                    <span>{item.label}</span>
                    <span className="portal-nav-item__soon">Soon</span>
                  </button>
                );
              }
              if (item.external) {
                return (
                  <a
                    key={item.label}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onNavigate}
                    className="portal-nav-item"
                    title={item.label}
                  >
                    <Icon aria-hidden />
                    <span>{item.label}</span>
                    <span className="sr-only">(opens in a new tab)</span>
                    <ExternalLink
                      aria-hidden
                      className="portal-nav-item__external"
                    />
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn("portal-nav-item", isActive && "is-active")
                  }
                  title={item.label}
                >
                  <Icon aria-hidden />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="portal-sidebar-foot">
        <AccountMenu
          placement="top-start"
          trigger={({ open, toggle, ref }) => (
            <button
              ref={ref}
              type="button"
              className="portal-user"
              aria-haspopup="menu"
              aria-expanded={open}
              onClick={toggle}
            >
              <span className="portal-avatar">{initialsFrom(displayName)}</span>
              <span className="portal-user__meta">
                <b>{displayName ?? "Account"}</b>
                <span>Dispatcher</span>
              </span>
              <ChevronDown
                aria-hidden
                style={{ transform: open ? "rotate(180deg)" : undefined }}
              />
            </button>
          )}
        />
        <button
          type="button"
          className="portal-collapse"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft aria-hidden />
          <span>Collapse</span>
        </button>
      </div>
    </aside>
  );
}
